import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { AVAILABLE_DAYS } from '../conventionDays';

// Short label for the day picker pills (e.g. "Fri Jul 10" -> "FRI 10").
// Falls back to the full label if it doesn't match the expected 3-part shape.
const pickerLabel = (label) => {
  const parts = (label || '').trim().split(/\s+/);
  return parts.length >= 3 ? `${parts[0]} ${parts[2]}` : label || '';
};

function SignUp() {
  const [timeslots, setTimeslots] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredSlots, setFilteredSlots] = useState([]);

  const [filtersOpen, setFiltersOpen] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: ''
  });

  const emptyFilters = {
    searchName: '',
    departmentId: '',
    // Array of YYYY-MM-DD strings. Empty = no day filter (show all).
    // Multiple = show shifts on any of the selected days.
    selectedDays: [],
    fromTime: '',
    toTime: ''
  };
  const [filters, setFilters] = useState(emptyFilters);

  const [globalMessage, setGlobalMessage] = useState('');
  const [slotMessages, setSlotMessages] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [newVolunteerCode, setNewVolunteerCode] = useState(null);
  const [persistentCode, setPersistentCode] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [bannerCopied, setBannerCopied] = useState(false);
  const [phoneConflictSlotId, setPhoneConflictSlotId] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [warpActive, setWarpActive] = useState(false);
  const [recentlySignedUp, setRecentlySignedUp] = useState(new Set());
  // The day the rail displays — the LATEST day (in document order) whose
  // banner header has been scrolled past (i.e., is now above the visible
  // content area). Empty string = no banner passed yet → rail hidden.
  // The rail only flips to a new day after that day's banner is also
  // passed, so it never disappears mid-day or while a banner is in view.
  const [railDay, setRailDay] = useState('');
  // Refs for the per-day banner headers, observed to drive the rail.
  const headerRefsRef = useRef({});
  // Set of day-IDs whose headers are currently above the viewport. Lifted
  // out of the IntersectionObserver closure so we can clear it imperatively
  // (e.g. on the back-to-top button click) when a smooth scroll doesn't
  // trigger intermediate observer events.
  const passedHeadersRef = useRef(new Set());


  useEffect(() => {
    fetchTimeslots();
    fetchDepartments();
  }, []);

  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [timeslots, filters, recentlySignedUp]);

  // Group filtered slots by YYYY-MM-DD and intersect with the canonical
  // AVAILABLE_DAYS list — that gives us a chronologically-ordered list of
  // days that actually have slots to show, using the canonical labels.
  const slotsByDay = {};
  for (const slot of filteredSlots) {
    const day = (slot.start_time || '').slice(0, 10);
    if (!day) continue;
    (slotsByDay[day] = slotsByDay[day] || []).push(slot);
  }
  const daysWithSlots = AVAILABLE_DAYS.filter(d => slotsByDay[d.date]);
  // Stable cache key for the rail-tracking effect dep — re-init the
  // observer whenever the rendered set of days changes.
  const dayKeys = daysWithSlots.map(d => d.date).join(',');


  // Second observer: tracks which banner headers have been scrolled past
  // (i.e., are above the visible content area below the sticky picker).
  // The rail shows the latest passed day in document order. Headers that
  // are still on screen, or still below the viewport entirely, do not
  // contribute — so the rail stays empty at the top of the page and only
  // switches to a new day after that day's banner has itself been passed.
  useEffect(() => {
    if (!dayKeys) {
      setRailDay('');
      return;
    }
    const dates = dayKeys.split(',');
    const els = dates.map(d => headerRefsRef.current[d]).filter(Boolean);
    if (els.length === 0) return;

    // Reset the shared "passed" tracking whenever the set of days changes
    // (filter toggle, fresh fetch, etc.) so we don't carry over stale ids.
    passedHeadersRef.current = new Set();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        const day = e.target.dataset.day;
        // "Passed" = the banner is not intersecting AND its top is above
        // the viewport (negative top). Headers still below the viewport
        // have a large positive top and are not yet passed.
        const isAbove = !e.isIntersecting && e.boundingClientRect.top < 0;
        if (isAbove) passedHeadersRef.current.add(day);
        else passedHeadersRef.current.delete(day);
      });
      // The rail shows the last day (in document order) whose banner has
      // been passed. Empty string => rail hidden.
      let latest = '';
      for (const d of dates) {
        if (passedHeadersRef.current.has(d)) latest = d;
      }
      setRailDay(latest);
    }, {
      // No sticky picker any more — the viewport itself is the threshold.
      threshold: 0,
    });
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [dayKeys]);


  const fetchTimeslots = async () => {
    try {
      const response = await axios.get(`${API_URL}/timeslots`);
      setTimeslots(response.data);
    } catch (error) {
      console.error('Error fetching timeslots:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API_URL}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const applyFilters = () => {
    const now = Date.now();
    let filtered = timeslots.filter(
      slot =>
        new Date(slot.end_time).getTime() > now &&
        (slot.current_signups < slot.max_volunteers || recentlySignedUp.has(slot.id))
    );

    if (filters.departmentId) {
      filtered = filtered.filter(
        slot => slot.department_id === parseInt(filters.departmentId)
      );
    }

    if (filters.searchName) {
      filtered = filtered.filter(slot => {
        const dept = departments.find(d => d.id === slot.department_id);
        return (
          dept &&
          dept.name.toLowerCase().includes(filters.searchName.toLowerCase())
        );
      });
    }

    // Day filter — multi-select pills. Empty array = no filter; otherwise
    // a shift must fall on one of the selected days (YYYY-MM-DD match).
    if (filters.selectedDays && filters.selectedDays.length > 0) {
      const allowed = new Set(filters.selectedDays);
      filtered = filtered.filter(s => allowed.has((s.start_time || '').slice(0, 10)));
    }

    // Time-of-day range filter — slot's start (HH:MM) within this clock window.
    if (filters.fromTime) {
      filtered = filtered.filter(s => (s.start_time || '').slice(11, 16) >= filters.fromTime);
    }
    if (filters.toTime) {
      filtered = filtered.filter(s => (s.start_time || '').slice(11, 16) <= filters.toTime);
    }

    filtered.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

    setFilteredSlots(filtered);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters(emptyFilters);
  };

  const setSlotMsg = (slotId, msg) => {
    setSlotMessages(prev => ({ ...prev, [slotId]: msg }));
  };

  const clearSlotMsg = (slotId, delay = 5000) => {
    setTimeout(() => setSlotMessages(prev => {
      const next = { ...prev };
      delete next[slotId];
      return next;
    }), delay);
  };

  const handleSignup = async (slotId, forceNew = false) => {
    if (loading) return;
    if (
      !formData.firstName.trim() ||
      !formData.lastName.trim() ||
      !formData.phone.trim()
    ) {
      setHasAttemptedSubmit(true);
      setGlobalMessage({
        text: 'Please enter your first name, last name, and phone number',
        type: 'error'
      });
      return;
    }

    const phoneRegex = /^[\d\s\-+()]{10,}$/;
    if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      setGlobalMessage({
        text: 'Please enter a valid phone number (at least 10 digits)',
        type: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      const fullName =
        `${formData.firstName.trim()} ${formData.lastName.trim()}`;

      const res = await axios.post(`${API_URL}/timeslots/${slotId}/signup`, {
        name: fullName,
        phone: formData.phone,
        ...(forceNew ? { force_new: true } : {})
      });

      const code = res.data.lookup_code;
      const isNew = res.data.is_new_volunteer;

      setRecentlySignedUp(prev => new Set(prev).add(slotId));
      setTimeout(() => {
        setRecentlySignedUp(prev => {
          const next = new Set(prev);
          next.delete(slotId);
          return next;
        });
      }, 3500);

      if (isNew) {
        setNewVolunteerCode(code);
      } else {
        setSlotMsg(slotId, {
          text: 'You\'re signed up!',
          type: 'success'
        });
        clearSlotMsg(slotId, 5000);
      }
      setTimeslots(prev => prev.map(s =>
        s.id === slotId ? { ...s, current_signups: s.current_signups + 1 } : s
      ));
    } catch (error) {
      if (error.response?.data?.error === 'phone_match_different_name') {
        setPhoneConflictSlotId(slotId);
      } else {
        const errorMsg = error.response?.data?.error || 'Error signing up';
        setSlotMsg(slotId, { text: errorMsg, type: 'error' });
        clearSlotMsg(slotId, 6000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDifferentPerson = async () => {
    const slotId = phoneConflictSlotId;
    setPhoneConflictSlotId(null);
    if (slotId != null) await handleSignup(slotId, true);
  };

  const getDepartmentName = (id) => {
    const dept = departments.find(d => d.id === id);
    return dept ? dept.name : 'Unknown Department';
  };

  const formatTimeRange = (startTimeStr, endTimeStr) => {
    const startDate = new Date(startTimeStr);
    const endDate = new Date(endTimeStr);

    const weekday = startDate.toLocaleString('en-US', { weekday: 'short' });
    const startMonth = startDate.toLocaleString('en-US', { month: 'short' });
    const startDay = startDate.getDate();
    const startTime = startDate.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

    const endMonth = endDate.toLocaleString('en-US', { month: 'short' });
    const endDay = endDate.getDate();
    const endTime = endDate.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

    if (startDay !== endDay) {
      return `${weekday}, ${startMonth} ${startDay} ${startTime} – ${endMonth} ${endDay} ${endTime}`;
    }
    return `${weekday}, ${startMonth} ${startDay} • ${startTime} – ${endTime}`;
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(newVolunteerCode).then(() => {
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2500);
    });
  };

  const handleDismissCodeModal = () => {
    setPersistentCode(newVolunteerCode);
    setNewVolunteerCode(null);
    setCodeCopied(false);
  };

  const handleCopyBannerCode = () => {
    navigator.clipboard.writeText(persistentCode).then(() => {
      setBannerCopied(true);
      setTimeout(() => setBannerCopied(false), 2500);
    });
  };

  const handleDismissBanner = () => {
    setPersistentCode(null);
    setBannerCopied(false);
  };

  return (
    <div className="signup-container">

      {phoneConflictSlotId !== null && (
        <div className="code-modal-overlay" onClick={() => setPhoneConflictSlotId(null)}>
          <div className="code-modal" onClick={e => e.stopPropagation()}>
            <h3 className="code-modal-title">Phone Already Registered</h3>
            <p className="code-modal-subtitle">
              This phone number is already registered under a different name.
            </p>
            <p className="code-modal-hint">
              If you've signed up before, please go back and use the same name spelling you used previously.
              <br /><br />
              If a family member shares this phone and you're a different person, click below to continue with your name.
            </p>
            <div className="code-modal-actions">
              <button className="code-modal-dismiss-btn" onClick={() => setPhoneConflictSlotId(null)}>
                Go Back & Fix Name
              </button>
              <button className="code-modal-copy-btn" onClick={handleConfirmDifferentPerson}>
                I'm a Different Person
              </button>
            </div>
          </div>
        </div>
      )}

      {persistentCode && !newVolunteerCode && (
        <div className="code-banner" role="status">
          <div className="code-banner-text">
            <span className="code-banner-label">YOUR VOLUNTEER CODE:</span>
            <span className="code-banner-code">{persistentCode}</span>
          </div>
          <div className="code-banner-actions">
            <button className="code-banner-copy" onClick={handleCopyBannerCode}>
              {bannerCopied ? '✓ COPIED' : 'COPY'}
            </button>
            <button className="code-banner-dismiss" onClick={handleDismissBanner} aria-label="Dismiss">✕</button>
          </div>
        </div>
      )}

      {newVolunteerCode && (
        <div className="code-modal-overlay" onClick={handleDismissCodeModal}>
          <div className="code-modal" onClick={e => e.stopPropagation()}>
            <div className="code-modal-icon">✓</div>
            <h3 className="code-modal-title">You're Signed Up!</h3>
            <p className="code-modal-subtitle">
              This is your personal volunteer code. You'll need it to look up your shifts.
            </p>
            <div className="code-modal-code">{newVolunteerCode}</div>
            <p className="code-modal-hint">
              Screenshot this or copy it — it won't be shown again.
            </p>
            <div className="code-modal-actions">
              <button className="code-modal-copy-btn" onClick={handleCopyCode}>
                {codeCopied ? '✓ Copied!' : 'Copy Code'}
              </button>
              <button className="code-modal-dismiss-btn" onClick={handleDismissCodeModal}>
                Got It
              </button>
            </div>
          </div>
        </div>
      )}

      <h2>Volunteer Sign Up</h2>

      {globalMessage && (
        <div className={`message ${globalMessage.type}`}>
          {globalMessage.text}
        </div>
      )}

      {/* VOLUNTEER INFO */}
      <div className="volunteer-info-section">
        <h3>Your Information</h3>

        <div className="form-row">
          <div className="form-group">
            <label>First Name *</label>
            <input
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Last Name *</label>
            <input
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Phone Number *</label>
          <input
            name="phone"
            type="tel"
            placeholder="(123) 456-7890"
            value={formData.phone}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        {hasAttemptedSubmit && (!formData.firstName.trim() || !formData.lastName.trim() || !formData.phone.trim()) && (
          <span className="alert-required">⚠ REQUIRED FIELDS INCOMPLETE ⚠</span>
        )}
      </div>

      {/* FILTERS + HEADER ROW */}
      <div className="slots-section">

        <div className="slots-header-row">
          <h3 className="slots-title">
            Volunteers Needed ({filteredSlots.length})
          </h3>

          <button
            className="filters-toggle-btn"
            onClick={() => setFiltersOpen(prev => !prev)}
          >
            Filters {filtersOpen ? '▾' : '▸'}
          </button>
        </div>

        {/* FILTER PANEL (collapsed by default) */}
        {filtersOpen && (
          <div className="filters-section">
            <div className="filters-grid">

              <div className="form-group">
                <label>Search by Department</label>
                <input
                  name="searchName"
                  value={filters.searchName}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="form-group">
                <label>Department</label>
                <select
                  name="departmentId"
                  value={filters.departmentId}
                  onChange={handleFilterChange}
                >
                  <option value="">All Departments</option>
                  {[...departments].sort((a, b) => a.name.localeCompare(b.name)).map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group filter-day-group">
                <label>Days</label>
                <div className="filter-day-pills" role="group" aria-label="Filter by day">
                  {AVAILABLE_DAYS.map(d => {
                    const on = filters.selectedDays.includes(d.date);
                    return (
                      <button
                        key={d.date}
                        type="button"
                        className={`day-pill ${on ? 'is-active' : ''}`}
                        aria-pressed={on}
                        onClick={() => setFilters(prev => ({
                          ...prev,
                          selectedDays: on
                            ? prev.selectedDays.filter(x => x !== d.date)
                            : [...prev.selectedDays, d.date]
                        }))}
                      >
                        {pickerLabel(d.label)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="form-group">
                <label>From Time</label>
                <input
                  type="time"
                  name="fromTime"
                  value={filters.fromTime}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="form-group">
                <label>To Time</label>
                <input
                  type="time"
                  name="toTime"
                  value={filters.toTime}
                  onChange={handleFilterChange}
                />
              </div>
            </div>

            <div className="filters-actions">
              <button type="button" className="filters-clear-btn" onClick={handleClearFilters}>
                Clear Filters
              </button>

            </div>
          </div>
        )}

        {/* DAY RAIL — vertical "you are here" label fixed to the left edge.
            Mobile-only (CSS-gated). Shows the latest passed day; stays put
            when scrolling into a new day until that new day's banner is
            also passed, so the indicator never disappears mid-scroll.
            Characters render as stacked spans so double-digit dates never
            get clipped by a writing-mode line box. */}
        {daysWithSlots.length > 1 && railDay && (() => {
          const found = AVAILABLE_DAYS.find(d => d.date === railDay);
          if (!found) return null;
          const parts = found.label.trim().split(/\s+/);
          const shortDay = (parts[0] || '').toUpperCase();
          const dayNum = parts[2] || '';
          return (
            <aside className="day-rail" aria-hidden="true">
              {shortDay.split('').map((ch, i) => (
                <span key={`d-${i}`} className="day-rail-char">{ch}</span>
              ))}
              {dayNum && <span className="day-rail-divider" />}
              {dayNum && dayNum.split('').map((ch, i) => (
                <span key={`n-${i}`} className="day-rail-char">{ch}</span>
              ))}
            </aside>
          );
        })()}

        {/* SLOTS */}
        {filteredSlots.length === 0 ? (
          <div className="no-slots">
            <p>No available time slots matching your filters.</p>
          </div>
        ) : (
          daysWithSlots.map(({ date }) => (
            <div
              key={date}
              data-day={date}
              className="day-section"
            >
              <h3
                className="day-section-header"
                ref={(el) => { headerRefsRef.current[date] = el; }}
                data-day={date}
              >
                {new Date(date + 'T12:00:00').toLocaleString('en-US', {
                  weekday: 'long', month: 'long', day: 'numeric'
                })}
              </h3>
              <div className="slots-grid">
                {slotsByDay[date].map(slot => (
                  <div key={slot.id} className={`slot-card ${slot.double_time ? 'slot-double-time' : ''}`}>

                    <div className="slot-header">
                      <h4>
                        {getDepartmentName(slot.department_id)}
                        {slot.double_time && <span className="slot-double-badge">2× Hours</span>}
                      </h4>
                      <span className="capacity-badge">
                        {slot.max_volunteers - slot.current_signups} spot
                        {slot.max_volunteers - slot.current_signups !== 1 ? 's' : ''} left
                      </span>
                    </div>

                    {slot.notes && (
                      <p className="slot-notes">{slot.notes}</p>
                    )}

                    {/* All "bottom" content lives in one block that's
                        anchored to the bottom of the card. Combined with a
                        fixed .slot-card height in CSS, this guarantees the
                        Time row, capacity bar, and Sign Up button sit at
                        the exact same vertical position on every card,
                        regardless of notes length. */}
                    <div className="slot-bottom">
                      <div className="slot-details">
                        <p>
                          <strong>Time:</strong>{' '}
                          {formatTimeRange(slot.start_time, slot.end_time)}
                        </p>

                        <div className="capacity-bar">
                          <div
                            className="capacity-fill"
                            style={{
                              width: `${(slot.current_signups / slot.max_volunteers) * 100}%`
                            }}
                          />
                        </div>

                        <p className="capacity-text">
                          {slot.current_signups} / {slot.max_volunteers} volunteers
                        </p>
                      </div>

                      {slotMessages[slot.id] && (
                        <div className={`slot-message ${slotMessages[slot.id].type}`}>
                          {slotMessages[slot.id].text}
                        </div>
                      )}

                      <button
                        className="signup-btn"
                        onClick={() => handleSignup(slot.id)}
                        disabled={loading}
                      >
                        {loading ? 'Signing up...' : 'Sign Up'}
                      </button>
                    </div>

                  </div>
                ))}
              </div>
            </div>
          ))
        )}

      </div>

      {(showBackToTop || warpActive) && (
        <>
          <button
            className={`back-to-top-btn${warpActive ? ' warp-launched' : ''}`}
            onClick={() => {
              if (warpActive) return;
              setWarpActive(true);
              setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                // Smooth scroll can skip the intermediate states the day-rail
                // observer relies on, leaving the rail stuck on the last day.
                // Clear the tracking + state imperatively so the rail hides
                // immediately; the observer will re-populate naturally on
                // the next downward scroll.
                passedHeadersRef.current.clear();
                setRailDay('');
              }, 1100);
              setTimeout(() => setWarpActive(false), 2200);
            }}
            aria-label="Back to top"
          >
            {warpActive && <div className="warp-trail" />}
            <img src="/enterprise.svg" alt="" className="back-to-top-icon" />
            <span className="back-to-top-label">TOP</span>
          </button>
        </>
      )}

    </div>
  );
}

export default SignUp;